import React, { useState, useRef } from "react";
import {
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
  IconButton,
  Tooltip,
} from "@mui/material";
import { ContentCopy, Check } from "@mui/icons-material";

import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { privateApi } from "../../api/axiosConfig";

export default function PaymentButton({ invoiceId }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState(null);

  const [copied, setCopied] = useState(false);
  const copyInputRef = useRef(null);

  const handleCopy = async () => {
    if (!paymentUrl) return;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(paymentUrl);
      } else if (copyInputRef.current) {
        // Fallback for HTTP / non-secure context inside MUI Dialog
        copyInputRef.current.select();
        copyInputRef.current.setSelectionRange(0, 99999); // Mobile Safari support
        document.execCommand("copy");
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  const handlePayment = async () => {
    try {
      setLoading(true);
      setError(null);

      // Pass the invoice ID if your backend requires it to know what order to process
      const response = await privateApi.post("/api/v1/payments/create/");

      if (response.data && response.data.payment_url) {
        // INSTEAD of redirecting automatically, save the URL to state
        setPaymentUrl(response.data.payment_url);
      } else {
        setError(
          response.data.message || "خطا در برقراری ارتباط با درگاه پرداخت.",
        );
      }
    } catch (err) {
      console.error("Payment initiation failed:", err);
      setError("مشکلی در ایجاد تراکنش به وجود آمد. لطفا دوباره تلاش کنید.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setPaymentUrl(null);
  };

  // The gateway return lands back on /invoices, and Zibal may hand the buyer
  // off to another tab. Staying in one tab keeps the flow predictable for the
  // user (the original tab waits at the invoice list instead of being
  // abandoned) and avoids depending on the token store being tab-shared.
  const handleGoToGateway = () => {
    if (!paymentUrl) return;
    window.location.assign(paymentUrl);
  };

  return (
    <>
      <Button
        variant="contained"
        color="success"
        size="large"
        disabled={loading}
        onClick={handlePayment}
        startIcon={
          loading ? (
            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
          ) : (
            <AccountBalanceWalletOutlinedIcon sx={{ ml: 1, mr: -0.5 }} />
          )
        }
        sx={{
          borderRadius: 2,
          fontWeight: 700,
          boxShadow: "0 4px 12px rgba(46, 125, 50, 0.2)",
          "&:hover": {
            boxShadow: "0 6px 16px rgba(46, 125, 50, 0.3)",
          },
        }}
      >
        {loading ? "در حال پردازش..." : "پرداخت آنلاین"}
      </Button>

    {/* Payment Info Dialog */}
    <Dialog
        open={Boolean(paymentUrl)}
        onClose={handleCloseDialog}
        dir="rtl"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          انتقال به درگاه پرداخت
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2, color: "text.primary" }}>
            لینک پرداخت شما با موفقیت ایجاد شد. آیا مایل هستید هم‌اکنون برای
            پرداخت به درگاه زیبال منتقل شوید؟
          </DialogContentText>

          <Alert
            severity="warning"
            sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}
          >
            توجه: برای جلوگیری از خطای درگاه پرداخت، لطفاً پیش از ادامه، 
             VPN یا  فیلترشکن خود را خاموش کنید.
          </Alert>

          {/* Hidden textarea inside Dialog tree to bypass MUI Focus Trap */}
          <textarea
            ref={copyInputRef}
            value={paymentUrl || ""}
            readOnly
            style={{ position: "fixed", opacity: 0, pointerEvents: "none", width: 1, height: 1 }}
          />

          <Alert
            severity="info"
            sx={{
              direction: "ltr",
              textAlign: "left",
              alignItems: "center",
              overflow: "hidden",
              "& .MuiAlert-message": {
                minWidth: 0,
                wordBreak: "break-all",
              },
            }}
            action={
              <Tooltip title={copied ? "Copied!" : "Copy link"}>
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={handleCopy}
                  aria-label="copy payment link"
                >
                  {copied ? (
                    <Check fontSize="small" />
                  ) : (
                    <ContentCopy fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            }
          >
            <Link
              href={paymentUrl}
              underline="hover"
              color="inherit"
              target="_blank"
              rel="noopener noreferrer"
            >
              {paymentUrl}
            </Link>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={handleCloseDialog}
            color="inherit"
            sx={{ fontWeight: 600 }}
          >
            انصراف
          </Button>

          {/* Same-tab navigation on purpose: see handleGoToGateway. */}
          <Button
            onClick={handleGoToGateway}
            disabled={!paymentUrl}
            variant="contained"
            color="success"
            endIcon={<OpenInNewIcon sx={{ mr: 1, ml: -0.5 }} />}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            ورود به درگاه زیبال
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: "100%", fontFamily: "inherit" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
